/**
 * Terms of Service Page
 * Clean, minimal design using on record Color Bible
 */

import LegalPageLayout from '../components/LegalPageLayout'

export default function TermsOfService() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="December 2025">
      <div className="space-y-12" style={{ color: '#111111', lineHeight: '1.75' }}>
        {/* Introduction */}
        <section>
          <p className="text-lg">
            Welcome to on record. By accessing or using our platform, you agree to be bound by these Terms of Service.
            Please read them carefully before using our services.
          </p>
        </section>

        {/* 1. Acceptance of Terms */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            1. Acceptance of Terms
          </h2>
          <p>
            By creating an account or using on record, you agree to these Terms of Service and our Privacy Policy.
            If you do not agree to these terms, please do not use our platform. We reserve the right to modify
            these terms at any time, and your continued use of the platform constitutes acceptance of any changes.
          </p>
        </section>

        {/* 2. Eligibility */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            2. Eligibility
          </h2>
          <p>
            You must be at least 13 years of age to use on record. By using our platform, you represent and warrant
            that you meet this age requirement and have the legal capacity to enter into these terms. If you are
            under 18, you confirm that you have obtained parental or guardian consent to use our services.
          </p>
        </section>

        {/* 3. User Accounts */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            3. User Accounts and Security
          </h2>
          <p className="mb-4">
            When you create an account on on record, you are responsible for maintaining the security of your account
            and for all activities that occur under your account. You agree to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your password secure and confidential</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Be responsible for all activities conducted through your account</li>
          </ul>
        </section>

        {/* 4. Content and Conduct */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            4. Content and Conduct
          </h2>
          <p className="mb-4">
            You are solely responsible for the content you post on on record. You agree not to post content that:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Is illegal, harmful, threatening, abusive, harassing, defamatory, or hateful</li>
            <li>Infringes on intellectual property rights or privacy rights of others</li>
            <li>Contains malicious code, viruses, or any harmful computer code</li>
            <li>Impersonates any person or entity or misrepresents your affiliation</li>
            <li>Is spam, unsolicited advertising, or promotional material</li>
            <li>Violates any applicable laws or regulations</li>
          </ul>
        </section>

        {/* 5. on record Code System */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            5. on record Code System
          </h2>
          <p>
            Each post on on record is assigned a unique 12-digit on record Code for verification and authenticity purposes.
            You agree that this code is a permanent identifier for your content and that on record may use it for content
            authentication, verification, and platform integrity purposes.
          </p>
        </section>

        {/* 6. Reporter Mode */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            6. Reporter Mode
          </h2>
          <p className="mb-4">
            on record offers a Reporter Mode feature that allows users to contribute content in response to platform needs.
            By enabling Reporter Mode, you agree to the following:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Your identity is protected through anonymous session IDs that rotate automatically</li>
            <li>You may receive task notifications for your general area, which you can accept or decline at your discretion</li>
            <li>Compensation for completed tasks, if offered, will be processed according to our payment terms</li>
            <li>Content you submit through Reporter Mode is subject to the same content guidelines as regular posts</li>
            <li>You retain ownership of content you create, subject to the license granted to on record</li>
            <li>on record administrators cannot identify you personally - they only see anonymous session identifiers</li>
          </ul>
          <p className="mt-4">
            Reporter Mode is designed with privacy-first principles. Your anonymous session ID changes every 2 hours and
            is immediately invalidated when you disable Reporter Mode. See our Privacy Policy for complete details on how
            your data is protected.
          </p>
        </section>

        {/* 7. Content Deletion and Evidence Preservation */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            7. Content Deletion and Evidence Preservation
          </h2>
          <p className="mb-4">
            on record is an evidence-first platform designed to preserve authentic news and media. By posting content publicly,
            you acknowledge and agree to the following:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                7.1 Public Content Becomes Evidence
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Once you post content publicly, it enters the on record Evidence Preservation system</li>
                <li>Public content may be cited, referenced, or used as evidence by journalists, researchers, and legal entities</li>
                <li>The on record Code assigned to your post serves as a permanent verification identifier</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                7.2 User Deletion Rights
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li><strong>Private Media:</strong> You have full deletion rights for unpublished media in your private folders</li>
                <li><strong>Post Visibility:</strong> You can hide your posts from public view at any time</li>
                <li><strong>Account Deletion:</strong> You may delete your account, which will hide all your posts</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                7.3 Limited Deletion for Public Content
              </h3>
              <p className="mb-3">
                By posting publicly on on record, you understand and agree that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>Deleting a post removes it from public view but does not guarantee permanent removal of media files</li>
                <li>Media files may be retained for up to 7 years to comply with law enforcement and legal requirements</li>
                <li>Thumbnails, metadata, and on record Codes are permanently preserved as verification records</li>
                <li>Complete and permanent deletion of public content requires administrator action</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                7.4 Shared Media
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>If you share media with other users who create posts from it, the media may be preserved for their use</li>
                <li>Deleting your original post does not delete copies or reposts made by others</li>
                <li>Reference counting ensures shared media remains available to other users who have access</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A2342' }}>
                7.5 Legal Compliance
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
                <li>We may retain content to comply with legal obligations, court orders, or law enforcement requests</li>
                <li>Content subject to legal hold cannot be deleted until the hold is released</li>
                <li>All deletion actions are logged for audit and compliance purposes</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 8. Intellectual Property */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            8. Intellectual Property Rights
          </h2>
          <p className="mb-4">
            You retain ownership of the content you post on on record. By posting content, you grant on record a worldwide,
            non-exclusive, royalty-free license to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Use, reproduce, modify, and display your content on the platform</li>
            <li>Distribute your content to other users according to your privacy settings</li>
            <li>Create derivative works for the purpose of displaying your content</li>
            <li>Use your content for promotional purposes (with your permission)</li>
          </ul>
        </section>

        {/* 9. Prohibited Activities */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            9. Prohibited Activities
          </h2>
          <p className="mb-4">
            You agree not to engage in any of the following activities:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: '#6B7280' }}>
            <li>Accessing or attempting to access systems or data without authorization</li>
            <li>Interfering with or disrupting the platform's functionality</li>
            <li>Scraping, crawling, or using automated means to access content</li>
            <li>Creating fake accounts or using bots</li>
            <li>Manipulating on record Codes or attempting to bypass verification systems</li>
            <li>Reverse engineering or attempting to extract source code</li>
          </ul>
        </section>

        {/* 10. Termination */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            10. Termination
          </h2>
          <p>
            We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason,
            including violation of these Terms of Service. Upon termination, your right to use on record will immediately
            cease. You may delete your account at any time through your account settings.
          </p>
        </section>

        {/* 11. Disclaimer of Warranties */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            11. Disclaimer of Warranties
          </h2>
          <p>
            on record is provided "as is" and "as available" without warranties of any kind, either express or implied.
            We do not warrant that the platform will be uninterrupted, error-free, or secure. Your use of on record is
            at your own risk.
          </p>
        </section>

        {/* 12. Limitation of Liability */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            12. Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by law, on record shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages arising out of or related to your use of the platform, even if we have
            been advised of the possibility of such damages.
          </p>
        </section>

        {/* 13. Governing Law */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            13. Governing Law and Dispute Resolution
          </h2>
          <p>
            These Terms of Service shall be governed by and construed in accordance with applicable laws. Any disputes
            arising out of or related to these terms shall be resolved through binding arbitration, except where prohibited
            by law.
          </p>
        </section>

        {/* 14. Changes to Terms */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            14. Changes to These Terms
          </h2>
          <p>
            We may update these Terms of Service from time to time. We will notify you of any material changes by posting
            the new terms on this page and updating the "Effective" date at the top. Your continued use of on record after
            such changes constitutes acceptance of the new terms.
          </p>
        </section>

        {/* 15. Contact */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#0A2342' }}>
            15. Contact Us
          </h2>
          <p className="mb-4">
            If you have questions about these Terms of Service, please contact us:
          </p>
          <div style={{ color: '#6B7280' }}>
            <p>Email: <a href="mailto:contact@onrecord.live" style={{ color: '#FFD43B' }} className="hover:underline">contact@onrecord.live</a></p>
          </div>
        </section>
      </div>
    </LegalPageLayout>
  )
}
